/**
 * THE TABLE — client.
 *
 * A full Tables + leaderboards backend shipped and sat unused: 373 passing tests
 * and not one line of it reachable from the app. This file is the wire.
 *
 * The one rule it inherits from the server, and the one that shapes every call
 * below: **you can only keep a table you have already sat at.** There is no
 * `createTable(roster)` here, and there must never be one — `keepTable` takes a
 * gameId, because a table crystallises out of a game that already happened. The
 * server refuses anything else (`tables.js: createTableFromGame`).
 *
 * Endpoints (all `verifyToken`):
 *   POST   /api/tables                                 keep this table
 *   GET    /api/tables                                 mine, last-played first
 *   GET    /api/tables/:id                             one (members only)
 *   PATCH  /api/tables/:id                             rename / re-emoji
 *   POST   /api/tables/:id/members                     bring someone
 *   DELETE /api/tables/:id/members/:uid                remove / leave
 *   GET    /api/tables/:id/games                       the album
 *   GET    /api/tables/:id/thread                      the thread
 *   POST   /api/tables/:id/thread                      verb one: reply
 *   POST   /api/tables/:id/thread/:postId/react        verb two: react
 *   DELETE /api/tables/:id/thread/:postId              tombstone
 *   GET    /api/tables/:id/leaderboard/week            this week at the Saeeds
 *   GET    /api/leaderboard/daily?scope=family|world   the Daily
 *   GET    /api/leaderboard/daily/me                   my band, not my rank
 *   GET    /api/leaderboard/hall-of-fame               all-time
 */

import { firebaseAuth, db } from './firebase';
import {
  collection, doc, query, where, orderBy, documentId, limit as fsLimit,
  type DocumentData, type DocumentReference, type DocumentSnapshot,
  type Query, type QuerySnapshot,
} from 'firebase/firestore';

/* ------------------------------------------------------------------ types */

export interface TableMember {
  name: string;
  avatar: string;
  role: 'owner' | 'member';
  joinedAt: number;
}

export interface PendingSeat {
  seatId: string;
  uid: string;
  name: string;
  avatar: string;
  expiresAtMs: number;
}

export interface Table {
  id: string;
  name: string;
  emoji: string;
  memberUids: string[];
  members: Record<string, TableMember>;
  pendingSeats?: PendingSeat[];
  createdBy: string;
  createdAtMs: number;
  lastPlayedAtMs: number;
  gameCount: number;
  settings?: { categories?: string[] | null; houseRules?: unknown };
}

/** A game record as the album reads it. */
export interface TableGame {
  id: string;
  roomCode?: string;
  rounds?: number;
  winnerId?: string | null;
  winnerName?: string | null;
  scores?: Record<string, number>;
  endedAtMs?: number;
  players?: Array<{ id: string; name: string; avatar: string; isBot?: boolean }>;
}

/**
 * A game NIGHT, not a game. The album is grouped: *"Friday, Nov 8 · 3 games ·
 * Maya won 2."* Families remember evenings, not room codes.
 */
export interface GameNight {
  dayKey: string;
  label: string;
  games: TableGame[];
  topWinnerName: string | null;
  topWinnerWins: number;
}

// `async_round` — ONE card per correspondence game, re-posted in place as the game
// advances (docId `async_${roomCode}`), so five rounds of turn-based play leave one
// living card in the thread rather than ten dead ones.
export type ThreadKind = 'game_night' | 'daily_match' | 'week_close' | 'async_round' | 'human';

export interface ThreadPost {
  id: string;
  kind: ThreadKind;
  authorUid: string | null;
  authorName?: string;
  authorAvatar?: string;
  text: string | null;
  payload?: any;
  reactions?: Record<string, string[]>;
  createdAtMs: number;
  deleted?: boolean;
}

export interface WeekRow {
  uid: string;
  name: string;
  avatar: string;
  score: number;
  rank: number;
  /** Top three only. There is no `last` — and there never will be. */
  medal: 'gold' | 'silver' | 'bronze' | null;
}

export interface Superlative {
  key: string;
  title: string;
  emoji: string;
  uid?: string;
  uids?: string[];
  name: string;
  detail: string;
}

export interface TableWeek {
  ok: boolean;
  tableId: string;
  weekKey: string;
  startMs: number;
  endMs: number;
  gamesPlayed: number;
  rows: WeekRow[];
  superlatives: Superlative[];
}

export interface DailyRow {
  uid: string;
  displayName: string;
  avatar: string;
  score: number;
  rank: number;
  medal?: 'gold' | 'silver' | 'bronze' | null;
  isYou?: boolean;
}

export interface MyDaily {
  played: boolean;
  date?: string;
  score?: number;
  total?: number;
  /** Null below the top 100 — you get a band instead. Deliberate. */
  rank?: number | null;
  band?: string | null;
  percentile?: number | null;
  delta?: number | null;
  topHundred?: boolean;
}

export interface HallOfFameRow {
  uid: string;
  userId: string;
  displayName: string;
  avatar: string;
  rankedScore: number;
  careerScore: number;
  totalGames: number;
  rankedGames: number;
  totalWins: number;
  rank: number;
  medal: 'gold' | 'silver' | 'bronze' | null;
}

/** The six. Fixed, forever — a reaction picker is not a feature surface. */
export const REACTIONS = ['😂', '❤️', '👏', '😮', '🔥', '🤨'] as const;

/** The curated set the server will accept (`tables.js: TABLE_EMOJI`). */
export const TABLE_EMOJI = ['🏠', '🎉', '🌟', '🍕', '☕', '🎲', '🦊', '🌻', '🏆', '❤️'] as const;

export const MAX_REPLY_CHARS = 280;

/* -------------------------------------------------------------- transport */

const API_BASE = (
  import.meta.env.VITE_API_BASE_URL ||
  (import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '')
).replace(/\/$/, '');

/** Thrown with the server's own machine-readable `error` code intact — the
 *  callers branch on `table-exists` and `needs-two-signed-in-humans`. */
export class TablesError extends Error {
  code: string;
  status: number;
  body: any;
  constructor(code: string, status: number, body: any) {
    super(code);
    this.name = 'TablesError';
    this.code = code;
    this.status = status;
    this.body = body;
  }
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (!API_BASE) throw new TablesError('no-api-base', 0, null);

  const token = await firebaseAuth.getCurrentUserToken();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init.headers || {}),
    },
  });

  const body = await res.json().catch(() => null);
  if (!res.ok) {
    throw new TablesError(body?.error || `http-${res.status}`, res.status, body);
  }
  return body as T;
}

/* ----------------------------------------------------------------- tables */

/**
 * KEEP THIS TABLE. The only door in.
 *
 * Takes the gameId of the night that just happened. On success the table comes
 * back with its album ALREADY FULL — the server walked backwards through
 * `gameRecords` and filed every past night this same roster played. That is the
 * whole feeling of the feature: the app noticed the family before the family
 * told it anything.
 */
export async function keepTable(
  gameId: string,
  opts: { name?: string; emoji?: string } = {},
): Promise<{ tableId: string; table: Table; backfill: { gamesFiled: number; cardsPosted: number } }> {
  return api('/tables', {
    method: 'POST',
    body: JSON.stringify({ gameId, name: opts.name, emoji: opts.emoji }),
  });
}

/**
 * The game record's id, from the room code.
 *
 * WHY THIS EXISTS. `keepTable` needs the gameRecords doc id, and the results
 * screen's `shareGameId` is fed by a `shareReady` socket event that **the
 * backend never emits** — it is declared in the frontend's event registry and
 * forwarded list, and nothing on the server sends it. So `shareGameId` is always
 * null in practice (which also means the ShareSheet has been running without an
 * id this whole time — worth a separate look by whoever owns it).
 *
 * Rather than change the backend (it is done, and it is not wrong — this is a
 * missing emit, not a missing route), we resolve the id the way the server
 * already lets us: `GET /api/games/:id` falls back to a bare ROOM CODE lookup
 * (`getGameRecordByRoomCode`), returns the newest record for that room, gates it
 * on participation, and hands back `{ id, ...record }`.
 *
 * Retried, because the record is written as the game ends and we may ask for it
 * in the same second.
 */
export async function resolveGameId(roomCode: string, attempts = 5): Promise<string | null> {
  if (!roomCode) return null;
  for (let i = 0; i < attempts; i++) {
    try {
      const rec = await api<{ id?: string }>(`/games/${roomCode}`);
      if (rec?.id) return rec.id;
    } catch {
      /* not written yet, or not ours — try again */
    }
    await new Promise(r => setTimeout(r, 1500));
  }
  return null;
}

export const listMyTables = () => api<Table[]>('/tables');

export const getTable = (tableId: string) => api<Table>(`/tables/${tableId}`);

export const renameTable = (tableId: string, patch: { name?: string; emoji?: string }) =>
  api<Table>(`/tables/${tableId}`, { method: 'PATCH', body: JSON.stringify(patch) });

export const addMember = (tableId: string, uid: string) =>
  api<Table>(`/tables/${tableId}/members`, { method: 'POST', body: JSON.stringify({ uid }) });

export const removeMember = (tableId: string, uid: string) =>
  api<{ ok: boolean; deleted?: boolean; table?: Table }>(`/tables/${tableId}/members/${uid}`, {
    method: 'DELETE',
  });

export const leaveTable = (tableId: string) =>
  api<{ ok: boolean; deleted?: boolean }>(`/tables/${tableId}/leave`, { method: 'POST' });

export const getAlbum = (tableId: string, limit = 50) =>
  api<TableGame[]>(`/tables/${tableId}/games?limit=${limit}`);

/* ----------------------------------------------------------------- thread */

export const getThread = (tableId: string, limit = 50) =>
  api<ThreadPost[]>(`/tables/${tableId}/thread?limit=${limit}`);

/** Verb one. ≤280 chars, screened server-side, rate-limited to 1 per 3s. */
export const postReply = (tableId: string, text: string) =>
  api<ThreadPost>(`/tables/${tableId}/thread`, {
    method: 'POST',
    body: JSON.stringify({ text: text.slice(0, MAX_REPLY_CHARS) }),
  });

/** Verb two. One of six, and it toggles. There is no third verb. */
export const react = (tableId: string, postId: string, emoji: string) =>
  api<{ ok: boolean; added: boolean; reactions: Record<string, string[]> }>(
    `/tables/${tableId}/thread/${postId}/react`,
    { method: 'POST', body: JSON.stringify({ emoji }) },
  );

export const deletePost = (tableId: string, postId: string) =>
  api<{ ok: boolean }>(`/tables/${tableId}/thread/${postId}`, { method: 'DELETE' });

/* ----------------------------------------------------------------- boards */

export const getTableWeek = (tableId: string) =>
  api<TableWeek>(`/tables/${tableId}/leaderboard/week`);

/** The family slice is the DEFAULT. The world lives behind it. */
export const getDailyBoard = (scope: 'family' | 'world' = 'family') =>
  api<{ date: string; scope: string; rows: DailyRow[] }>(`/leaderboard/daily?scope=${scope}`);

export const getMyDaily = () => api<MyDaily>('/leaderboard/daily/me');

export const getHallOfFame = (limit = 100) =>
  api<HallOfFameRow[]>(`/leaderboard/hall-of-fame?limit=${limit}`);

/* ------------------------------------------------------------------ shape */

/**
 * Group the album into NIGHTS. *"Friday, Nov 8 · 3 games · Maya won 2."*
 *
 * Local-date keyed, because a family's Friday is their Friday, not UTC's.
 * Reverse-chron: the last night you played is the one you want to see.
 */
export function toGameNights(games: TableGame[]): GameNight[] {
  const byDay = new Map<string, TableGame[]>();

  for (const g of games) {
    const ms = g.endedAtMs || 0;
    if (!ms) continue;
    const d = new Date(ms);
    const dayKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (!byDay.has(dayKey)) byDay.set(dayKey, []);
    byDay.get(dayKey)!.push(g);
  }

  return [...byDay.entries()]
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([dayKey, nightGames]) => {
      const wins = new Map<string, number>();
      for (const g of nightGames) {
        if (!g.winnerName) continue;
        wins.set(g.winnerName, (wins.get(g.winnerName) || 0) + 1);
      }
      const top = [...wins.entries()].sort((a, b) => b[1] - a[1])[0];

      const when = new Date(nightGames[0].endedAtMs || 0);
      const label = when.toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'short',
        day: 'numeric',
      });

      return {
        dayKey,
        label,
        games: nightGames.sort((a, b) => (b.endedAtMs || 0) - (a.endedAtMs || 0)),
        topWinnerName: top ? top[0] : null,
        topWinnerWins: top ? top[1] : 0,
      };
    });
}

/**
 * Does this exact roster already have a table?
 *
 * The "Keep this table?" card must not appear for people who already kept it.
 * Exact-match on the signed-in roster, which is the same test the server runs
 * in `findTableByExactRoster` — so the client's offer and the server's 409
 * agree, and nobody is offered a table they'll be refused.
 */
export function findExactRoster(tables: Table[], roster: string[]): Table | null {
  const want = [...new Set(roster)].sort();
  if (want.length === 0) return null;
  return (
    tables.find(t => {
      const mine = [...(t.memberUids || [])].sort();
      return mine.length === want.length && mine.every((u, i) => u === want[i]);
    }) || null
  );
}


/** Members, ordered so the avatar stack reads owner-first. */
export function memberList(table: Table): Array<TableMember & { uid: string }> {
  return (table.memberUids || [])
    .map(uid => ({
      uid,
      ...(table.members?.[uid] || { name: 'Player', avatar: '👤', role: 'member' as const, joinedAt: 0 }),
    }))
    .sort((a, b) => Number(b.role === 'owner') - Number(a.role === 'owner'));
}

/* ------------------------------------------------------------------- live
 *
 * THE READ SIDE, SUBSCRIBED.
 *
 * Everything above this line is the REST API, and every WRITE stays there
 * forever: the server owns the profanity screen, the rate limit, the back-fill
 * and the system cards, and `firestore.rules` denies client writes to `tables`
 * and `thread` on purpose.
 *
 * But the READS were one-shot, and that is the bug the owner actually hit —
 * *"i added someone and messages had to reload the page to show."* The documents
 * behind those reads are already client-readable; `firestore.rules:264` says so
 * out loud. So the reads become subscriptions to the same documents the REST
 * routes serve, and the shapes below are IDENTICAL to what those routes return —
 * the server builds its responses with `{ id: doc.id, ...doc.data() }` and so do
 * we (`tables.js: getTableById`, `listThread`). One shape, two transports, no
 * drift.
 *
 * Cost: every query here is bounded — by membership, by a `limit`, or by a
 * document-id `in` list. Nothing subscribes to an open collection.
 */

/** My tables. `array-contains` on membership — the rule's `list` is open to
 *  signed-in callers precisely so this query can filter itself. */
export function myTablesQuery(uid: string): Query<DocumentData> | null {
  if (!db || !uid) return null;
  return query(collection(db, 'tables'), where('memberUids', 'array-contains', uid));
}

/** One table: roster, name, emoji, counters. `get` is members-only. */
export function tableRef(tableId: string): DocumentReference<DocumentData> | null {
  if (!db || !tableId) return null;
  return doc(db, 'tables', tableId);
}

/** The thread, newest first. Same order and same page size as `GET /thread`. */
export function threadQuery(tableId: string, max = 50): Query<DocumentData> | null {
  if (!db || !tableId) return null;
  return query(
    collection(db, 'tables', tableId, 'thread'),
    orderBy('createdAtMs', 'desc'),
    fsLimit(Math.min(Math.max(max, 1), 100)),
  );
}

export const mapTable = (snap: DocumentSnapshot<DocumentData>): Table | null =>
  snap.exists() ? ({ id: snap.id, ...(snap.data() as Omit<Table, 'id'>) }) : null;

/**
 * Sorted by last played — an empty table quietly sinks, exactly as the REST
 * route does it (`tables.js: listMyTables`). The sort is in memory, not in the
 * query, and deliberately so: `array-contains` + `orderBy` on a second field
 * would demand a composite index for what is, for any real person, a handful of
 * documents.
 */
export const mapTables = (snap: QuerySnapshot<DocumentData>): Table[] =>
  snap.docs
    .map(d => ({ id: d.id, ...(d.data() as Omit<Table, 'id'>) }))
    .sort((a, b) => (b.lastPlayedAtMs || 0) - (a.lastPlayedAtMs || 0));

export const mapThread = (snap: QuerySnapshot<DocumentData>): ThreadPost[] =>
  snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<ThreadPost, 'id'>) }));

/* --------------------------------------------------------- the Daily, live */

/**
 * THE DAILY BOARD IS NOT A SNAPSHOT OF A FINISHED THING — it is still being
 * scored while you look at it, and that is not a nicety, it is the rule of the
 * game. A Daily score is a function of the whole day's pool (`daily.js`: *"your
 * Zebra is worth 10 if nobody else wrote Zebra"*), so when a stranger submits at
 * noon, YOUR score can move. The server re-scores the entire board on every
 * submission (`rescoreBoard`).
 *
 * A board that needs F5 to show that is not merely stale — it is showing you a
 * number that is no longer your score. This is the single strongest case for a
 * listener in the app.
 *
 * The world's top 100, in the board's real order: score desc, then EARLIEST
 * SUBMISSION WINS. Index-covered (`firestore.indexes.json`, collectionGroup
 * `entries`) and hard-capped at 100 documents.
 */
export const DAILY_BOARD_LIMIT = 100;

export function dailyBoardQuery(date: string, max = DAILY_BOARD_LIMIT): Query<DocumentData> | null {
  if (!db || !date) return null;
  return query(
    collection(db, 'dailyBoards', date, 'entries'),
    orderBy('score', 'desc'),
    orderBy('submittedAtMs', 'asc'),
    fsLimit(Math.min(Math.max(max, 1), DAILY_BOARD_LIMIT)),
  );
}

/**
 * The FAMILY slice — the default tab, and it cannot be a filter over the world's
 * top 100, because the whole point is that Grandma appears whether or not she is
 * in the world's top 100. So we ask for exactly the people at my tables, by
 * document id (the entry doc id IS the uid — that is the one-attempt rule).
 *
 * Chunked at 30 because that is Firestore's ceiling for an `in` filter. A real
 * family is one chunk, i.e. ONE listener; the chunking exists so that somebody
 * who sits at five big tables degrades to two listeners instead of breaking.
 */
export const DAILY_FAMILY_CHUNK = 30;

export function dailyFamilyQueries(date: string, uids: string[]): Array<Query<DocumentData>> {
  if (!db || !date || uids.length === 0) return [];
  const unique = [...new Set(uids.filter(Boolean))];
  const out: Array<Query<DocumentData>> = [];
  for (let i = 0; i < unique.length; i += DAILY_FAMILY_CHUNK) {
    out.push(
      query(
        collection(db, 'dailyBoards', date, 'entries'),
        where(documentId(), 'in', unique.slice(i, i + DAILY_FAMILY_CHUNK)),
      ),
    );
  }
  return out;
}

/** My own sheet on the day's board. One document — the cheapest possible signal
 *  that the referee has re-scored me because somebody else played. */
export function dailyEntryRef(date: string, uid: string): DocumentReference<DocumentData> | null {
  if (!db || !date || !uid) return null;
  return doc(db, 'dailyBoards', date, 'entries', uid);
}

/** The raw entry as the server writes it (`daily.js: rescoreBoard`). */
export interface DailyEntry {
  uid: string;
  score: number;
  displayName: string;
  avatar: string;
  submittedAtMs: number;
  cells?: unknown;
}

export const mapDailyEntries = (snap: QuerySnapshot<DocumentData>): DailyEntry[] =>
  snap.docs.map(d => ({ uid: d.id, ...(d.data() as Omit<DailyEntry, 'uid'>) }));

/**
 * Score descending, then EARLIEST SUBMISSION WINS — stated here in code because
 * it is stated in code on the server too (`leaderboards.js: byScoreThenEarliest`,
 * `daily.js: sortBoard`), and a tiebreak that lives only in an index definition
 * is a tiebreak the next query quietly forgets.
 */
export const byScoreThenEarliest = (a: DailyEntry, b: DailyEntry): number =>
  (b.score || 0) - (a.score || 0) || (a.submittedAtMs || 0) - (b.submittedAtMs || 0);

/** Entries → the rows the board renders, ranked and medalled. Mirrors
 *  `getDailyBoard` / `getDailyFamilySlice` exactly, so the live board and the
 *  REST board can never disagree about who is first. */
export function toDailyRows(entries: DailyEntry[], me?: string, medals = true): DailyRow[] {
  return [...entries].sort(byScoreThenEarliest).map((e, i) => ({
    uid: e.uid,
    displayName: e.displayName || 'Player',
    avatar: e.avatar || '👤',
    score: e.score || 0,
    rank: i + 1,
    medal: medals ? (i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : null) : null,
    isYou: me ? e.uid === me : undefined,
  }));
}

/** Everyone at any of my tables, plus me. The family, as `getDailyFamilySlice`
 *  defines it — derived from the live tables list, so a table you were added to
 *  five seconds ago is already in your family slice. */
export function familyUidsOf(tables: Table[], me: string): string[] {
  const family = new Set<string>(me ? [me] : []);
  for (const t of tables) for (const uid of t.memberUids || []) family.add(uid);
  return [...family];
}
